"use client";

import { useState } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Typography,
} from "@mui/material";
import {
  Menu as MenuIcon,
  TableChart,
  ExitToApp,
  QueryStats,
  DeviceThermostat,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";

export default function Navbar({ onLogout }) {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const navItems = [
    {
      label: "Sensores",
      icon: <DeviceThermostat />,
      path: "/sensores",
    },
    {
      label: "Gráficos",
      icon: <QueryStats />,
      path: "/graficos",
    },
    {
      label: "Tabla",
      icon: <TableChart />,
      path: "/tabla",
    },
  ];

  const handleDrawerClose = () => setDrawerOpen(false);

  const navButtons = navItems.map(({ label, icon, path }) => {
    const isActive = location.pathname === path;

    return (
      <Button
        key={path}
        color="inherit"
        component={Link}
        to={path}
        disabled={isActive}
        sx={{
          display: "flex",
          alignItems: "center",
          position: "relative",
          mx: 0.5,
          px: 2,
          py: 1,
          borderRadius: 2,
          transition: "all 0.3s ease",
          color: "white",
          backgroundColor: isActive
            ? "rgba(255, 255, 255, 0.1)"
            : "transparent",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.15)",
            color: "white",
            transform: "translateY(-1px)",
            "&::after": {
              width: isActive ? "100%" : "80%",
              opacity: isActive ? 1 : 0.6,
            },
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -2,
            left: "50%",
            transform: "translateX(-50%)",
            width: isActive ? "100%" : "0%",
            height: "3px",
            backgroundColor: "white",
            borderRadius: "2px",
            transition: "all 0.3s ease",
            opacity: isActive ? 1 : 0,
            boxShadow: isActive ? "0 0 8px rgba(255, 255, 255, 0.5)" : "none",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(45deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))",
            borderRadius: "inherit",
            opacity: isActive ? 1 : 0,
            transition: "opacity 0.3s ease",
          },
          "& > *": {
            position: "relative",
            zIndex: 1,
          },
        }}
        onClick={handleDrawerClose}
      >
        {icon}
        <Box sx={{ ml: 1 }}>{label}</Box>
      </Button>
    );
  });

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          {/* Logo y hamburguesa */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: isMobile ? "100%" : "350px",
            }}
          >
            {isMobile && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => setDrawerOpen(true)}
                sx={{
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
              <img src="/logo-2.png" alt="Compulab" style={{ height: 60 }} />
            </Link>

            <Typography
              variant="h7"
              fontWeight="bold"
              sx={{ color: "white", marginLeft: 2, justifyContent: "flex-end" }}
            >
              Semaloma S.A.
            </Typography>
          </Box>

          {/* Botones de navegación para escritorio */}
          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {navButtons}
            </Box>
          )}

          {!isMobile && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: "350px",
              }}
            >
              <Button
                color="inherit"
                onClick={onLogout}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <ExitToApp sx={{ mr: 1 }} />
                Cerrar sesión
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Drawer lateral para móviles */}
      <Drawer anchor="left" open={drawerOpen} onClose={handleDrawerClose}>
        <Box
          sx={{
            width: 280,
            bgcolor: "background.paper",
            height: "100%",
            background: "linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)",
          }}
          role="presentation"
          onClick={handleDrawerClose}
          onKeyDown={handleDrawerClose}
        >
          <List sx={{ pt: 2 }}>
            {navItems.map(({ label, icon, path }) => {
              const selected = location.pathname === path;
              return (
                <ListItem
                  key={path}
                  component={Link}
                  to={path}
                  sx={{
                    mx: 2,
                    mb: 1,
                    borderRadius: 2,
                    position: "relative",
                    color: "text.primary",
                    transition: "all 0.3s ease",
                    ...(selected && {
                      bgcolor: "primary.main",
                      color: "white",
                      transform: "translateX(8px)",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: -16,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 4,
                        height: "60%",
                        backgroundColor: "primary.main",
                        borderRadius: "0 4px 4px 0",
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 8,
                        height: 8,
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        borderRadius: "50%",
                        animation: "pulse 2s infinite",
                      },
                    }),
                    "&:hover": {
                      bgcolor: selected
                        ? "primary.dark"
                        : "rgba(25, 118, 210, 0.08)",
                      transform: selected
                        ? "translateX(8px) scale(1.02)"
                        : "translateX(4px)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: selected ? "white" : "primary.main",
                      minWidth: 40,
                    }}
                  >
                    {icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={label}
                    sx={{
                      "& .MuiListItemText-primary": {
                        fontWeight: selected ? 600 : 400,
                      },
                    }}
                  />
                </ListItem>
              );
            })}
          </List>

          <Divider sx={{ mx: 2, my: 2 }} />

          <List>
            <ListItem
              button
              onClick={onLogout}
              sx={{
                mx: 2,
                borderRadius: 2,
                color: "text.secondary",
                transition: "all 0.3s ease",
                "&:hover": {
                  bgcolor: "rgba(211, 47, 47, 0.08)",
                  color: "error.main",
                  transform: "translateX(4px)",
                },
              }}
            >
              <ListItemIcon sx={{ color: "inherit", minWidth: 40 }}>
                <ExitToApp />
              </ListItemIcon>
              <ListItemText primary="Cerrar sesión" />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Estilos CSS para la animación */}
      <style jsx global>{`
        @keyframes pulse {
          0%,
          100% {
            opacity: 0.8;
            transform: translateY(-50%) scale(1);
          }
          50% {
            opacity: 1;
            transform: translateY(-50%) scale(1.2);
          }
        }
      `}</style>
    </>
  );
}
